import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import {
  OnboardingStep,
  subscribeToOnboardingConfig,
  subscribeToUserOnboardingProgress,
  updateUserOnboardingProgress
} from '@/services/onboardingService';

export const OverallProgressCard: React.FC = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [onboardingConfig, setOnboardingConfig] = useState<{
    flow?: { steps: OnboardingStep[] };
  } | null>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  const [progress, setProgress] = useState(0);

  // Flatten all steps and substeps for easier processing
  const allSteps = React.useMemo(() => {
    if (!onboardingConfig?.flow?.steps) return [];

    return onboardingConfig.flow.steps.flatMap(step =>
      step.substeps ? [step, ...step.substeps] : [step]
    ).filter(step => step.type === 'modal'); // Only include interactive steps
  }, [onboardingConfig]);

  // Calculate progress and current step
  useEffect(() => {
    if (!allSteps.length) return;

    const completedCount = allSteps.filter(step =>
      completedSteps.includes(step.id)
    ).length;

    const newProgress = Math.round((completedCount / allSteps.length) * 100);
    setProgress(newProgress);

    // Find the first incomplete step
    const nextStep = allSteps.find(step => !completedSteps.includes(step.id)) ||
      allSteps[allSteps.length - 1];
    setCurrentStep(nextStep);
  }, [allSteps, completedSteps]);

  // Subscribe to onboarding config changes
  useEffect(() => {
    if (!user) return;

    const unsubscribeConfig = subscribeToOnboardingConfig((config) => {
      if (config) {
        setOnboardingConfig(config);
      }
      setLoading(false);
    });

    return () => unsubscribeConfig();
  }, [user]);

  // Subscribe to user progress changes
  useEffect(() => {
    if (!user) return;

    const unsubscribeProgress = subscribeToUserOnboardingProgress(
      user.uid,
      (steps) => {
        setCompletedSteps(steps);
      }
    );

    return () => unsubscribeProgress();
  }, [user]);

  const markStepComplete = useCallback(async (stepId: string) => {
    if (!user) return false;

    try {
      return await updateUserOnboardingProgress(user.uid, stepId, true);
    } catch (error) {
      console.error('Error updating onboarding progress:', error);
      return false;
    }
  }, [user]);

  const handleContinue = useCallback(async () => {
    if (!currentStep) return;

    await markStepComplete(currentStep.id);
    // The subscription will update the current step automatically
  }, [currentStep, markStepComplete]);

  if (loading || !onboardingConfig) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Loading Onboarding...</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }
  if (!currentStep) {
    return (
      <Card className="w-full">
        <CardContent className="p-6 text-center">
          <p>No onboarding steps found. Please check your configuration.</p>
        </CardContent>
      </Card>
    );
  }

  // Find current step index for the progress indicator
  const currentStepIndex = onboardingConfig?.flow?.steps.findIndex(
    step => step.id === currentStep.id
  ) ?? 0;

  return (
    <>
      <div className="text-xl font-bold ml-4 mb-4">Overall Progress</div>
      {/* Step Indicator */}
      <div className="mb-6 flex items-center justify-between">
        {onboardingConfig?.flow?.steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep.id === step.id;
          const isPast = currentStepIndex > index;

          console.log(step.id, isCompleted, isCurrent, isPast);

          return (
            <div key={step.id} className="relative flex flex-1 flex-col items-center">
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-300",
                  isPast
                    ? "bg-purple-600 text-white"
                    : isCurrent
                      ? "bg-purple-500 text-white"
                      : "bg-gray-200 text-gray-600"
                )}
              >
                {isPast ? <Check className="h-5 w-5" /> : stepNumber}
              </div>
              <div
                className={cn(
                  "mt-2 text-center text-sm font-medium",
                  isPast || isCurrent ? "text-gray-800" : "text-gray-500"
                )}
              >
                {step.title}
              </div>
              {index < (onboardingConfig.flow.steps.length - 1) && (
                <div
                  className={cn(
                    "absolute top-5 left-[calc(50%+20px)] h-0.5 w-[calc(100%-40px)] -translate-y-1/2 bg-gray-200 transition-colors duration-300",
                    isPast && "bg-purple-400"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Current Step Content */}
      {/* <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {currentStep.title}
          </h3>
          <p className="text-gray-600 mb-6">
            {currentStep.description}
          </p>
          
          {currentStep.substeps && currentStep.substeps.length > 0 && (
            <div className="mt-4 space-y-2">
              {currentStep.substeps.map((substep) => (
                <div 
                  key={substep.id}
                  className={cn(
                    'p-3 rounded-md border',
                    completedSteps.includes(substep.id)
                      ? 'bg-green-50 border-green-100'
                      : 'bg-white border-gray-200',
                    'flex items-center justify-between'
                  )}
                >
                  <div>
                    <h4 className="font-medium">{substep.title}</h4>
                    <p className="text-sm text-gray-500">{substep.description}</p>
                  </div>
                  {completedSteps.includes(substep.id) ? (
                    <div className="bg-green-100 text-green-600 p-1 rounded-full">
                      <Check className="h-4 w-4" />
                    </div>
                  ) : (
                    <button
                      onClick={() => markStepComplete(substep.id)}
                      className="px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                    >
                      Start
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleContinue}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
            >
              {completedSteps.includes(currentStep.id) ? 'Next Step' : 'Mark Complete'}
            </button>
          </div>
        </div> */}
    </>
  );
};

export default OverallProgressCard;
